import { useProfile } from "~/store/profile";

type Coupon = {
  card_number: string;
  short_code: string;
  ts_cpn_assign: string;
  ts_prog_exp: string;
  unique_code: string;
  used: boolean;
  used_at: string | null;
  expired: boolean;
  expires_at?: string | null;
};

type PoolCoupon = {
  id: number;
  unique_code: string;
  short_code: string;
  pool: string | null;
  assigned_to: string | null;
  assigned_at: string | null;
  activated_at: string | null;
  used: boolean | null;
  used_at: string | null;
  expired: boolean | null;
  expires_at: string | null;
};

type TierData = {
  label: string;
  points: number;
  return_period: number;
  free_shipping_min_total: number;
};

type Settings = {
  enabled: boolean;
  program_date_start: string;
  program_date_end: string;
  points_per_coupon: number;
  coupon_redemption_date_start: string;
  coupon_redemption_date_end: string;
  coupon_lifetime: number;
  tiers: Record<string, TierData>;
};

type FidelityData = {
  tier: string;
  next_tier: string | null;
  points: number;
  coupons: Coupon[];
  pool_coupons: Record<string, PoolCoupon[]>;
  settings: Settings;
};

export const useFidelityStore = defineStore("fidelity", () => {
  let inFlight: Promise<FidelityData | null> | null = null;
  let latestRequest = 0;

  const profileStore = useProfile();
  const fidelityData = ref<FidelityData | null>(null);
  const hasLoaded = ref(false);
  const isFetching = computed(() => !fidelityData.value && !hasLoaded.value);

  // Pass force to refetch after something server-side changed the balance;
  // it bypasses both the cached value and any request already in flight,
  // since those predate the change.
  const fetchFidelityData = async (force = false) => {
    if (fidelityData.value && !force) {
      return fidelityData.value;
    }

    if (inFlight && !force) {
      return inFlight;
    }

    const requestId = ++latestRequest;

    inFlight = $fetch<FidelityData>("/api/fidelity", {
      method: "POST",
      body: {
        cardNumber: profileStore.profile.codiceCard ?? '111',
      },
    })
      .then((data) => {
        // A forced refresh may have overtaken this response; its data is
        // newer, so leave it alone.
        if (requestId !== latestRequest) {
          return fidelityData.value;
        }

        // The endpoint answers {} for a missing or malformed card; leave the
        // state empty so a later call with a real card can still succeed.
        fidelityData.value = data?.settings ? data : null;

        return fidelityData.value;
      })
      .catch(() => null)
      .finally(() => {
        // Set even on failure, so an endpoint that keeps answering {} shows
        // the empty state instead of a skeleton that never resolves.
        hasLoaded.value = true;

        if (requestId === latestRequest) {
          inFlight = null;
        }
      });

    return inFlight;
  };

  const getFidelityData = async (force = false) => {
    if (!fidelityData.value || force) {
      await fetchFidelityData(force);
    }

    return fidelityData.value;
  };

  const points = computed(() => fidelityData.value?.points ?? 0);

  const pointDifference = computed(() =>
    fidelityData?.value?.tier &&
      fidelityData?.value?.next_tier &&
      fidelityData?.value?.settings
      ? fidelityData.value.settings.tiers[fidelityData.value.next_tier].points -
      fidelityData.value.points
      : 200
  );

  const isGold = computed(() => fidelityData.value?.tier === "gold");

  const pointsPerCoupon = computed(() =>
    fidelityData.value?.settings
      ? fidelityData.value?.settings?.points_per_coupon
      : 200
  );

  const nextTierPoints = computed(() =>
    fidelityData?.value?.next_tier && fidelityData?.value?.settings
      ? fidelityData.value.settings.tiers[fidelityData.value.next_tier].points
      : 200 + points.value
  );

  const nextTierStatusLabel = computed(() =>
    fidelityData?.value?.next_tier && fidelityData.value?.settings
      ? fidelityData.value.settings.tiers[fidelityData?.value?.next_tier].label
      : "lover gold"
  );

  const currentTierStatusLabel = computed(() =>
    fidelityData?.value?.tier && fidelityData.value?.settings
      ? fidelityData.value.settings.tiers[fidelityData?.value?.tier].label
      : "standart"
  );

  const standardReturnPeriod = computed(
    () => fidelityData.value?.settings?.tiers?.standard?.return_period ?? 30
  );

  const goldReturnPeriod = computed(
    () => fidelityData.value?.settings?.tiers?.gold?.return_period ?? 40
  );

  // Upstream sends 0 when the option is unset; fall back to the legacy 90 days.
  const couponLifetime = computed(
    () => fidelityData.value?.settings?.coupon_lifetime || 90
  );

  const couponRedemptionDateEnd = computed(() =>
    fidelityData?.value &&
      fidelityData.value?.settings &&
      fidelityData.value?.settings.coupon_redemption_date_end
      ? fidelityData.value?.settings.coupon_redemption_date_end
      : ""
  );

  const programDateEnd = computed(() =>
    fidelityData?.value &&
      fidelityData.value?.settings &&
      fidelityData.value?.settings.program_date_end
      ? fidelityData.value?.settings.program_date_end
      : ""
  );

  const pointsDiffFromPrevCoupon = () => {
    const ppc = pointsPerCoupon.value;
    if (ppc <= 0 || points.value < 0) return 0;
    return points.value % ppc;
  };

  const pointsDiffFromNextCoupon = () => {
    const ppc = pointsPerCoupon.value;
    if (ppc <= 0 || points.value < 0) return 0;
    return ppc - pointsDiffFromPrevCoupon();
  };

  return {
    fidelityData,
    hasLoaded,
    isFetching,
    fetchFidelityData,
    getFidelityData,
    pointsDiffFromPrevCoupon,
    pointsDiffFromNextCoupon,
    points,
    pointDifference,
    isGold,
    nextTierPoints,
    pointsPerCoupon,
    nextTierStatusLabel,
    currentTierStatusLabel,
    standardReturnPeriod,
    goldReturnPeriod,
    couponLifetime,
    couponRedemptionDateEnd,
    programDateEnd,
  };
});
